import * as THREE from "three";
import Scenes from "oc-threejs/Scenes";
import {PString} from "../properties/PString";
import {PAny} from "../properties/PAny";
import Debugs from "oc-modules/oc-threejs/Debugs";
import {PColor} from "../properties/PColor";
import {PNumber} from "../properties/PNumber";
import {PVector3} from "../properties/PVector3";
import {PEuler} from "../properties/PEuler";
import {PBoolean} from "../properties/PBoolean";
import 'three/examples/js/lights/RectAreaLightUniformsLib.js';
import "three/examples/js/helpers/RectAreaLightHelper";
import {json, MetadataRow} from "../../global";
import {ShadowNode} from "./light/ShadowNode";
import {PSubNode} from "../properties/PSubNode";
import Maths3 from "oc-threejs/Maths3";
import {v3} from "oc-threejs/short3";
import {AbstractMetaJsonNode} from "./AbstractMetaJsonNode";
import {ThreeMixin} from "./ThreeMixin";
import {GenericMetaNodeConstructor} from "./GenericMetaNode";

let uniformsInitialized = false;

export class LightJson extends ThreeMixin<GenericMetaNodeConstructor, THREE.Light>(AbstractMetaJsonNode) {

    id: number;
    _Type: string;
    shadow = new ShadowNode(this);
    castShadow: boolean;
    visible: boolean;
    _previousTypeConstructor: THREE.Light.constructor;
    _previousCastShadow: boolean;
    helper3d: THREE.Group;
    shadowCameraHelper3d: THREE.Object3D;
    showHelper = false;
    positionInCameraSpace: boolean;
    useFixReflectionPoint: boolean;
    fixReflectionRotation: THREE.Euler;
    fixReflectionDistance: number;

    // constructor(metaTable, meta) {
    //     super(metaTable, meta);
    //     // this.threeValue = threeValue;
    //     // this.fromThree(this.threeValue);
    //     // folgendes, damit die Vergleichswerte vorher/nachher initialisiert sind
    //     // this.changed('json', () => this.toJSON());
    //     // this.changed('Type');
    // }

    get Type() {
        return this._Type;
    }

    set Type(type) {
        if (this._Type == type) {
            return;
        }
        this._Type = type ?? 'AmbientLight';
        Scenes.remove(this.threeValue);
        this.threeValue = new THREE[this._Type]();
    }

    init(): this {
        this.fromMetadataRow(this.meta);
        this.updateScene();
        return this;
    }

    onRemove() {
        Scenes.remove(this.threeValue);
    }

    fillWithDefaults() {
        this.fromThree(this.threeValue);
        return this;
    }

    fromMetadataRow(row: MetadataRow) {
        return super.fromMetadataRow(row);
    }

    toThree(instance) {
        super.toThree(instance);
        if (!this.isShadowType()) {
            instance.castShadow = false;
        }
    }

    fromJSON(json: json = {}, filter = p => p.type) {
        super.fromJSON(json);
        return this;
    }

    getProperties() {
        if (this._properties == null || this._previousTypeConstructor !== this.threeValue?.constructor || this._previousCastShadow !== this.threeValue?.castShadow) {
            this._resetProperties();
            this._previousTypeConstructor = this.threeValue?.constructor;
            this._previousCastShadow = this.threeValue?.castShadow;
        }
        return this._properties;
    }

    isShadowType() {
        return ["DirectionalLight", "PointLight", "SpotLight"].includes(this.Type);
    }

    get castShadowReal() {
        return this.castShadow && this.isShadowType();
    }

    updateScene() {
        this._dataChanged = {};
        if (this.changed('Type')) {
            if (!uniformsInitialized && this.Type == 'RectAreaLight') {
                THREE.RectAreaLightUniformsLib.init();
                uniformsInitialized = true;
            }
            this.threeValue.__sceneNodeParentEntry = {
                node: this,
                parentNodeList: APP.sceneNode.findProperty('LightJson')
            };
            APP.scene.add(this.threeValue);
        }

        const changedJson = this.changed('json', () => this.toJSON());
        if (changedJson) {
            this.toThree(this.threeValue);
            if (this.threeValue.decay !== undefined) {
                this.threeValue.decay = 2;
            }
            if (this.castShadowReal && this.threeValue?.shadow?.camera) {
                this.threeValue.shadow.camera.updateProjectionMatrix();
                this.threeValue.needsUpdate = true;
                APP.sceneNode.triggers.add('ShadowCamera');
                APP.trigger('shadowLightPositionChanged', this.threeValue);
            }
        }

        if (this.changed('useFixReflectionPoint') || this.changed('positionInCameraSpace')) {
            if (this.useFixReflectionPoint) {
                const plane = Maths3.plane.y0;
                this.threeValue.onBeforeRenderStage = () => {
                    const raycaster = new THREE.Raycaster();
                    raycaster.setFromCamera(v3(), STAGE.camera);
                    const reflectionPoint = raycaster.ray.intersectPlane(plane);
                    if (reflectionPoint == null) {
                        return;
                    }
                    const camToReflection = reflectionPoint.clone().sub(STAGE.camera.position);

                    const reflected = camToReflection.clone().reflect(plane.normal);

                    reflected.applyEuler(new THREE.Euler().fromArray(this.fixReflectionRotation));

                    if (Math.abs(this.fixReflectionDistance) > 0.0001) {
                        reflected.normalize().multiplyScalar(this.fixReflectionDistance);
                    }

                    const newLightPosition = reflected.add(reflectionPoint);

                    this.threeValue.position.copy(newLightPosition);

                    this.threeValue.updateMatrixWorld();

                    if (this.threeValue.target == null) {
                        this.threeValue.lookAt(reflectionPoint);
                    } else {
                        this.threeValue.target.position.copy(reflectionPoint);
                    }

                    if (this.castShadowReal) {
                        APP.trigger('shadowLightPositionChanged', this.threeValue);
                    }
                }
            } else {
                if (this.positionInCameraSpace) {
                    APP.camera.add(this.threeValue);
                    this.threeValue.onBeforeRenderStage = () => {
                        if (this.castShadowReal) {
                            APP.trigger('shadowLightPositionChanged', this.threeValue);
                        }
                    };
                } else {
                    APP.scene.add(this.threeValue);
                    this.threeValue.onBeforeRenderStage = () => {
                    };
                }
            }
        }

        this.updateHelper();
    }

    updateHelper() {
        const maybeUpdate = h => {
            h?.update && _.delay(() => {
                h.update();
                STAGE.dirty();
            }, 300);
        }

        if (this.changed('showHelper') || this.changed('Type') || this.changed('visible')) {
            Scenes.remove(this.helper3d);
            this.helper3d = null;
            if (this.showHelper && this.visible) {
                this.helper3d = Debugs.lightHelper(this.threeValue, 1);
                this.helper3d.dependsOnObjectsArray = null;
                APP.scene.add(this.helper3d);
            }
        } else {
            maybeUpdate(this.helper3d);
        }

        if (this.changed('castShadowReal') || this.changed('showHelper') || this.changed('Type') || this.changed('visible')) {
            Scenes.remove(this.shadowCameraHelper3d);
            this.shadowCameraHelper3d = null;
            const shadowCamera = this.threeValue?.shadow?.camera;
            if (this.showHelper && this.castShadowReal && shadowCamera && this.visible) {
                this.shadowCameraHelper3d = new THREE.CameraHelper(shadowCamera);
                APP.scene.add(this.shadowCameraHelper3d);
            }
        } else {
            maybeUpdate(this.shadowCameraHelper3d);
        }
    }

    /**
     * Dient der Strukturierung und Ergänzung vorhandener Properties, welche mittels Reflektion aus material3d ermittelt werden
     */
    static propertyGruppierung = [
        {
            group: I18N("scene editor:light group"), open: true
        },
        {name: 'Type'},
        {name: "name"},
        {
            name: "color",
            propertyClass: PColor,
            default: "#ffffff",
            comment: "Color of the light. Defaults to white."
        },
        {
            name: "groundColor",
            propertyClass: PColor,
            default: "#ffffff",
            comment: "The light's ground color. Defaults to white."
        },
        {
            name: "intensity",
            min: 0,
            max: 10,
            default: 1,
            propertyClass: PNumber,
            comment: "The light's intensity, or strength. In physically correct mode, the units of intensity depend on the type of light. Default is 1.0."
        },
        {
            name: "position",
            propertyClass: PVector3,
            default: [0, 1, 0],
            comment: "Position of the light",
            showInEditor: (p) => !["AmbientLight", "HemisphereLight"].includes(p.node.Type)
        },
        {
            name: "target",
        },
        {
            name: "width",
            min: 0,
            max: 50,
            default: 1,
            propertyClass: PNumber,
            comment: "The width of the area light rectangle."
        },
        {
            name: "height",
            min: 0,
            max: 50,
            default: 0.5,
            propertyClass: PNumber,
            comment: "The height of the area light rectangle."
        },
        {
            name: "rotation",
            propertyClass: PEuler,
            default: [0, 0, 0],
            comment: "Rotation of the light",
            showInEditor: (p) => "RectAreaLight" == p.node.Type
        },
        {
            name: "angle",
            propertyClass: PNumber,
            min: 0,
            max: Math.PI / 2,
            default: Math.PI / 3,
            inputScale: 180 / Math.PI,
            comment: "Maximum extent of the spotlight, in radians, from its direction. Should be no more than Math.PI/2. The default is Math.PI/3."
        },
        {
            name: "penumbra",
            propertyClass: PNumber,
            min: 0,
            max: 1,
            default: 0,
            comment: "Percent of the spotlight cone that is attenuated due to penumbra. Takes values between 0 and 1. The default is 0."
        },
        {
            name: "visible",
            comment: "If set to false the light will be turned off",
            propertyClass: PBoolean,
            default: true,
        },
        {
            name: "castShadow",
            propertyClass: PBoolean,
            default: true,
            comment: "If set to true light will cast dynamic shadows",
            showInEditor: (p) => p.node.isShadowType(),
        },
        {separator: true},
        {
            name: "positionInCameraSpace",
        },
        {separator: true},
        {
            "name": "useFixReflectionPoint",
        },
        {
            "name": "fixReflectionRotation",
        },
        {
            "name": "fixReflectionDistance",
        },
        {
            group: I18N("scene editor:shadow group")
        },
        {
            name: "shadow",
        },
    ];

    createProperties() {
        let properties = [
            new PAny({
                name: 'id',
                type: 'Id',
                node: this,
                showInEditor: (p) => false,
                isThree: (p) => false,
            }),
            new PString({
                name: 'Type',
                selectOptions: Object.keys(THREE).filter(k => THREE[k].prototype instanceof THREE.Light &&
                    !/Probe/.test(k)),
                default: "DirectionalLight",
                node: this,
                isThree: (p) => false
            }),
            new PBoolean({
                name: "positionInCameraSpace",
                comment: "Interpretiert die Position im Camera-Bezugsystem statt im Welt-Bezugssystem (also wie eine Stirnlampe). z-Koordinate in Blickrichtung ist negativ im Cameraspace!",
                default: false,
                node: this,
                showInEditor: (p) => !["AmbientLight", "HemisphereLight"].includes(p.node.Type) && !p.node.useFixReflectionPoint,
                isThree: (p) => false,
            }),
            new PBoolean({
                "name": "useFixReflectionPoint",
                "comment": "Fester Reflektionspunkt: Wenn gesetzt, dann wird das Licht so positioniert, dass sein Reflektionspunkt auf dem Boden in der Bildmitte ist",
                default: false,
                node: this,
                showInEditor: (p) => !["AmbientLight", "HemisphereLight"].includes(p.node.Type) && !p.node.positionInCameraSpace,
                isThree: (p) => false,
            }),
            new PEuler({
                "name": "fixReflectionRotation",
                "comment": "Fester Reflektionspunkt: Der Euler-Winkel, um den das Licht zusätzlich rotiert wird",
                default: [0, 0, 0],
                node: this,
                showInEditor: (p) => p.node.useFixReflectionPoint && !["AmbientLight", "HemisphereLight"].includes(p.node.Type),
                isThree: (p) => false,
            }),
            new PNumber({
                "name": "fixReflectionDistance",
                "comment": "Fester Reflektionspunkt: Abstand des Lichtes vom Reflektionspunkt. Falls 0, dann der Kamera-Abstand.",
                default: 0,
                node: this,
                showInEditor: (p) => p.node.useFixReflectionPoint && !["AmbientLight", "HemisphereLight"].includes(p.node.Type),
                isThree: (p) => false,
            }),
        ];
        properties = properties.concat(this.getGenericPropertiesFromThreejs(this.threeValue, (key, value) => {
            if ('name' == key) {
                return new PString({
                    name: "name",
                    node: this,
                });

            }
            if ('target' == key) {
                return new PVector3({
                    name: "target",
                    default: [0, 0, 0],
                    comment: "the Light points from its position to target. The default position is (0, 0, 0).",
                    node: this,
                    showInEditor: (p) => ["DirectionalLight", "SpotLight"].includes(p.node.Type),
                    toThree<ThreeInstanceType>(instance: THREE.Light) {
                        const target = this.get();
                        if (instance['target']) {
                            instance['target'].position.fromArray(target);
                            if (!Scenes.isInScene(instance['target'])) {
                                APP.scene.add(instance['target']);
                            }
                        }
                    },
                    fromThree<ThreeInstanceType>(instance: THREE.Material) {
                        if (instance['target']) {
                            const value = instance['target'].position.toArray();
                            this.set(value);
                        }
                    }
                });
            }
            if ('shadow' == key) {
                return new PSubNode({
                    name: "shadow",
                    label: null,
                    node: this,
                    showInEditor: (p) => p.node.castShadowReal,
                });
            }
            if (/^(scale|decay|distance|power|receiveShadow|quaternion|layers|animations|sh|needsUpdate|onBeforeRenderStage)$/g.test(key)) {
                return null;
            }
            return undefined;
        }));
        return this.orderAndGroup(
            LightJson.propertyGruppierung,
            properties
        );
    }

    getTableName() {
        return 'LightJson';
    }

}
